import { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import axios from 'axios';
import { Package, ChevronRight, Loader2, ShoppingBag, Truck, CheckCircle2, Clock, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  pending: { label: 'Order Placed', color: 'text-amber-600', icon: Clock, bg: 'bg-amber-50' },
  paid: { label: 'Confirmed', color: 'text-blue-600', icon: CheckCircle2, bg: 'bg-blue-50' },
  shipped: { label: 'Shipped', color: 'text-indigo-600', icon: Truck, bg: 'bg-indigo-50' },
  delivered: { label: 'Delivered', color: 'text-green-600', icon: CheckCircle2, bg: 'bg-green-50' },
  cancelled: { label: 'Cancelled', color: 'text-red-500', icon: XCircle, bg: 'bg-red-50' },
};

export default function ShopOrdersPage() {
  const { user } = useOutletContext();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) fetchOrders();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      const { data } = await axios.get(`${API}/shop/orders`, { headers: { Authorization: `Bearer ${token}` } });
      setOrders(Array.isArray(data) ? data : []);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }} data-testid="orders-not-logged-in" className="shop-page">
        <Package className="w-12 h-12 mx-auto text-orange-400 mb-3" />
        <h2 className="text-lg font-bold mb-1">Login to view your orders</h2>
        <p className="text-sm text-gray-500 mb-4">Track all your past purchases</p>
        <Link to="/auth" className="inline-block px-5 py-2.5 bg-[#FF7A00] text-white font-bold rounded-lg" data-testid="orders-login-cta">
          Login
        </Link>
      </div>
    );
  }

  return (
    <div className="shop-page" data-testid="orders-page" style={{ paddingBottom: 100 }}>
      <div className="shop-fk-section" style={{ marginTop: 8 }}>
        <h1 className="shop-fk-section-title">My Orders</h1>
        <p className="shop-fk-section-subtitle">{orders.length} order{orders.length !== 1 ? 's' : ''}</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
        </div>
      ) : orders.length === 0 ? (
        <div className="shop-fk-section" data-testid="orders-empty">
          <div className="text-center py-10">
            <ShoppingBag className="w-14 h-14 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-bold text-gray-800 mb-1">No orders yet</h3>
            <p className="text-sm text-gray-500 mb-5">Looks like you haven't placed an order yet.</p>
            <Link to="/shop" className="inline-flex items-center gap-1 px-5 py-2.5 bg-[#FF7A00] text-white font-bold rounded-lg text-sm" data-testid="orders-shop-now">
              Shop Now <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      ) : (
        orders.map((order) => {
          const status = STATUS_META[order.status] || STATUS_META.pending;
          const Icon = status.icon;
          const itemCount = (order.items || []).length;
          const date = order.created_at ? new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
          return (
            <Link
              to={`/shop/order/${order.id}`}
              key={order.id}
              className="shop-fk-section block"
              data-testid={`order-card-${order.id}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${status.bg}`}>
                  <Icon className={`w-5 h-5 ${status.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between mb-1">
                    <span className={`text-xs font-bold ${status.color}`}>{status.label}</span>
                    <span className="text-[11px] text-gray-400">{date}</span>
                  </div>
                  <div className="text-sm font-semibold text-gray-900 truncate">
                    {(order.items?.[0]?.product_name) || 'DharmShop Order'}
                    {itemCount > 1 && (
                      <span className="text-gray-500 font-normal"> +{itemCount - 1} more</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-xs text-gray-500">
                      Order #{(order.id || '').slice(0, 8).toUpperCase()}
                    </span>
                    <span className="text-sm font-bold text-gray-900">₹{order.total_amount || order.total || 0}</span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300 mt-2.5 flex-shrink-0" />
              </div>
            </Link>
          );
        })
      )}
    </div>
  );
}
